<template>
  <div class="container-fluid">
    <Breadcrumbs main="Generate Sessions CSV" :path="breadcrumbPath" />

    <div
      v-if="$store.state.auth.user != null && $store.state.auth.user.is_admin"
      class="row m-0 py-3 width-100-p bg-me-primary border-color-light rounded-4"
    >
      <el-col :span="24">
        <h5 class="text-white mt-3 ml-1">Generate Sessions CSV</h5>
        <span class="moderat-white-12 ml-1">
          * Select a monitoring period and category, choose one or more customers, then generate CSV exports.
        </span>
      </el-col>

      <div class="custom-filter-container pr-20">
        <el-row>
          <div class="custom-filter-container-wrapper d-flex row y-center m-0 mb-12 width-100-p">
            <el-form :label-position="`top`" class="custom-filter-form d-flex">
              <el-space class="custom-filter-space" fill>
                <el-row gutter="20">
                  <el-col
                    :span="$vuetify.display.width > 1280 ? 10 : 24"
                    :class="$vuetify.display.width > 645 ? 'x-start' : 'x-center'"
                  >
                    <el-form-item class="custom-filter-item label-style" prop="mp" label="Monitoring Period">
                      <el-select
                        v-model="selectedMpId"
                        placeholder="Select Monitoring Period"
                        class="custom-filter-select"
                        filterable
                        clearable
                        :loading="monitoringPeriodsLoading"
                        @change="handleMonitoringPeriodChange"
                        @clear="clearMonitoringPeriod"
                      >
                        <el-option
                          v-for="item in monitoringPeriods"
                          :key="item.id"
                          :label="item.name || item.status_display"
                          :value="item.id"
                        >
                          <span>{{ item.name == null ? item.status_display : item.name }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col
                    :span="$vuetify.display.width > 1280 ? 10 : 24"
                    :class="$vuetify.display.width > 645 ? 'x-start' : 'x-center'"
                  >
                    <el-form-item class="custom-filter-item label-style" prop="category" label="Category">
                      <el-select
                        v-model="selectedCategory"
                        placeholder="Select Category"
                        class="custom-filter-select"
                        filterable
                        @change="handleCategoryChange"
                      >
                        <el-option
                          v-for="item in categoryOptions"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        />
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col
                    :span="$vuetify.display.width > 1280 ? 6 : 24"
                    :class="$vuetify.display.width > 645 ? 'x-start' : 'x-center'"
                  >
                    <el-form-item class="custom-filter-item label-style" label="Selected Customers">
                      <el-tag type="success" size="large">
                        {{ selectedCustomerRows.length }} selected
                      </el-tag>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-space>
            </el-form>
          </div>
        </el-row>
      </div>
    </div>

    <el-row class="m-0 width-100-p mt-3">
      <el-col class="d-flex align-item-center" :span="24">
        <div class="row m-0 width-100-p">
          <div class="row m-0 py-3 width-100-p bg-me-primary border-color-light rounded-4">
            <el-col :span="24">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="text-white mb-1">Available Customers</h5>
                  <span class="moderat-white-12">
                    Select customers and choose an enabled split option for each selected row.
                  </span>
                </div>

                <el-button
                  class="custom-filter-button-apply"
                  :loading="generating"
                  :disabled="!canGenerate"
                  @click="submitGenerateRequest"
                >
                  <span class="moderat-btn-apply pr-3"> Generate </span>
                  <i class="fa fa-angle-right"></i>
                </el-button>
              </div>
            </el-col>

            <el-col :span="24">
              <div
                v-loading="customersLoading"
                class="custom-table-design"
                element-loading-background="rgba(122, 122, 122, 0.8)"
              >
                <el-table
                  ref="customersTable"
                  :data="customers"
                  :fit="true"
                  height="400"
                  stripe
                  row-key="_selectionKey"
                  class="my-2 data-table"
                  empty-text="Select a monitoring period to load available customers"
                  @selection-change="handleSelectionChange"
                >
                  <el-table-column type="selection" width="55" />
                  <el-table-column label="Customer" prop="full_name" min-width="220" sortable />
                  <el-table-column label="Total Sessions" prop="total_count" width="150" sortable>
                    <template #default="scope">
                      {{ formatNumber(scope.row.total_count) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Available Split Options" min-width="260">
                    <template #default="scope">
                      <el-button
                        v-for="option in getEnabledSplitOptions(scope.row)"
                        :key="option.value"
                        class="mr-1 mb-1"
                        size="small"
                        :style="selectedSplits[scope.row._selectionKey] === option.value ? splitButtonSelectedStyle : splitButtonUnselectedStyle"
                        @click.stop="selectSplitOption(scope.row, option.value)"
                      >
                        {{ option.label }}
                      </el-button>
                      <span v-if="getEnabledSplitOptions(scope.row).length === 0" class="text-white">
                        No options available
                      </span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-col>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- <el-row v-if="generatedFiles.length > 0" class="m-0 width-100-p mt-3">
      <el-col class="d-flex align-item-center" :span="24">
        <div class="row m-0 width-100-p">
          <div class="row m-0 py-3 width-100-p bg-me-primary border-color-light rounded-4">
            <el-col :span="24">
              <h5 class="text-white mb-3">Generated Files</h5>
            </el-col>
            <el-col :span="24">
              <div class="custom-table-design">
                <el-table :data="generatedFiles" :fit="true" height="400" stripe class="my-2 data-table">
                  <el-table-column label="Customer" prop="customer_name" min-width="220" sortable />
                  <el-table-column label="Category" min-width="190" sortable>
                    <template #default="scope">
                      {{ getCategoryLabel(scope.row.category) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Split By" min-width="150">
                    <template #default="scope">
                      {{ getSplitLabel(scope.row.split_by) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Split Range" min-width="300" sortable>
                    <template #default="scope">
                      {{ scope.row.start_time }} - {{ scope.row.end_time }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Rows" prop="row_count" width="130" sortable>
                    <template #default="scope">
                      {{ formatNumber(scope.row.row_count) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="File" min-width="220">
                    <template #default="scope">
                      <a :href="scope.row.url" target="_blank" rel="noopener">
                        {{ scope.row.file || scope.row.url }}
                      </a>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-col>
          </div>
        </div>
      </el-col>
    </el-row> -->
  </div>
</template>

<script>
import Breadcrumbs from "../../../components/bread_crumbs.vue";
import UserService from "../../../services/user.service";

export default {
  name: "GenerateSessionsFileCsv",
  components: {
    Breadcrumbs,
  },
  data() {
    return {
      breadcrumbPath: [
        {
          title: "Operation",
          disabled: true,
          href: "",
        },
        {
          title: "Generate Sessions CSV",
          disabled: false,
          href: "/generate-sessions-file-csv",
          active: true,
        },
      ],
      monitoringPeriods: [],
      selectedMpId: null,
      selectedCategory: "sessions-with-abatement",
      customers: [],
      selectedCustomerRows: [],
      selectedSplits: {},
      // generatedFiles: [],
      monitoringPeriodsLoading: false,
      customersLoading: false,
      generating: false,
      categoryOptions: [
        { value: "sessions", label: "Sessions" },
        { value: "unallocated-sessions", label: "Unallocated Sessions" },
        { value: "sessions-with-abatement", label: "Sessions With Abatement" },
      ],
      splitOptions: [
        { value: "no_split", label: "No Split" },
        { value: "semi_annual", label: "Semi Annual" },
        { value: "quarterly", label: "Quarterly" },
        { value: "monthly", label: "Monthly" },
      ],
      splitButtonUnselectedStyle: {
        backgroundColor: "#547477",
        borderColor: "#234B49",
        color: "#fff",
        fontFamily: "Moderat",
        fontWeight: "400",
      },
      splitButtonSelectedStyle: {
        backgroundColor: "#ddf0ac",
        borderColor: "#ddf0ac",
        color: "#0D3937",
        fontFamily: "Moderat",
        fontWeight: "500",
      },
    };
  },
  computed: {
    canGenerate() {
      return (
        this.selectedMpId != null &&
        this.isValidCategory(this.selectedCategory) &&
        this.selectedCustomerRows.length > 0 &&
        this.selectedCustomerRows.every((customer) => this.isSelectedCustomerReady(customer))
      );
    },
  },
  mounted() {
    this.fetchMonitoringPeriods();
  },
  methods: {
    async fetchMonitoringPeriods() {
      this.monitoringPeriodsLoading = true;
      try {
        const cached = sessionStorage.getItem("monitoringPeriods");
        if (cached) {
          this.monitoringPeriods = JSON.parse(cached);
          return;
        }

        const response = await UserService.getMonitoringPeriods(null, "", 1, 50, true);
        const data = response.data.monitoring_periods || [];
        this.monitoringPeriods = data;
        if (data.length > 0) {
          sessionStorage.setItem("monitoringPeriods", JSON.stringify(data));
        }
      } catch (error) {
        this.handleApiError(error, "Failed to load monitoring periods.");
      } finally {
        this.monitoringPeriodsLoading = false;
      }
    },

    handleMonitoringPeriodChange(mpId) {
      this.selectedMpId = mpId;
      // this.generatedFiles = [];
      this.fetchAvailableCustomers();
    },

    handleCategoryChange() {
      // this.generatedFiles = [];
      this.fetchAvailableCustomers();
    },

    clearMonitoringPeriod() {
      this.selectedMpId = null;
      this.customers = [];
      this.selectedCustomerRows = [];
      this.selectedSplits = {};
      // this.generatedFiles = [];
    },

    async fetchAvailableCustomers() {
      if (this.selectedMpId == null || this.selectedMpId === "") {
        return;
      }

      this.customersLoading = true;
      this.customers = [];
      this.selectedCustomerRows = [];
      this.selectedSplits = {};

      try {
        const response = await UserService.getListOfSessionsWithAbatement({
          mp_id: this.selectedMpId,
          category: this.selectedCategory,
        });
        this.customers = (response.data.customers || []).map((customer, index) => ({
          ...customer,
          _selectionKey: this.buildCustomerSelectionKey(customer, index),
        }));
        if (response.data.message) {
          this.$message.success(response.data.message);
        }
      } catch (error) {
        this.handleApiError(error, "Failed to load available customers.");
      } finally {
        this.customersLoading = false;
      }
    },

    handleSelectionChange(rows) {
      this.selectedCustomerRows = rows;
      const selectedIds = rows.map((row) => this.getCustomerKey(row));

      Object.keys(this.selectedSplits).forEach((customerId) => {
        if (!selectedIds.includes(customerId)) {
          delete this.selectedSplits[customerId];
        }
      });

    },

    getCustomerKey(customer) {
      return customer._selectionKey;
    },

    buildCustomerSelectionKey(customer, index) {
      const customerId = customer.id ?? customer.customer_id;
      return customerId != null ? `${customerId}-${index}` : `row-${index}`;
    },

    getCustomerId(customer) {
      return customer.id ?? customer.customer_id;
    },

    isValidCategory(category) {
      return this.categoryOptions.some((option) => option.value === category);
    },

    getCategoryLabel(value) {
      const option = this.categoryOptions.find((item) => item.value === value);
      return option ? option.label : value;
    },

    getEnabledSplitOptions(customer) {
      const rules = customer.rules || {};
      return this.splitOptions.filter((option) => rules[option.value] === true);
    },

    isSplitAllowed(customer, splitBy) {
      return this.getEnabledSplitOptions(customer).some((option) => option.value === splitBy);
    },

    isSelectedCustomerReady(customer) {
      const customerId = this.getCustomerId(customer);
      const splitBy = this.selectedSplits[this.getCustomerKey(customer)];
      return customerId != null && splitBy != null && this.isSplitAllowed(customer, splitBy);
    },

    isCustomerSelected(customer) {
      return this.selectedCustomerRows.some((row) => this.getCustomerKey(row) === this.getCustomerKey(customer));
    },

    selectSplitOption(customer, splitBy) {
      if (!this.isSplitAllowed(customer, splitBy)) {
        this.$message.error("This split option is not available for the selected customer.");
        return;
      }
      this.selectedSplits[this.getCustomerKey(customer)] = splitBy;
      if (!this.isCustomerSelected(customer)) {
        this.$refs.customersTable?.toggleRowSelection(customer, true);
      }
    },

    getSplitLabel(value) {
      const option = this.splitOptions.find((item) => item.value === value);
      return option ? option.label : value;
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },

    validateGenerateRequest() {
      if (this.selectedMpId == null || this.selectedMpId === "") {
        return "Monitoring period is required.";
      }

      if (!this.isValidCategory(this.selectedCategory)) {
        return "Invalid category selected.";
      }

      if (this.customers.length === 0) {
        return "No customers are available for the selected monitoring period and category.";
      }

      if (this.selectedCustomerRows.length === 0) {
        return "Please select at least one customer.";
      }

      const customerIds = this.selectedCustomerRows.map((customer) => this.getCustomerId(customer));
      if (customerIds.some((customerId) => customerId == null || customerId === "")) {
        return "Each selected customer must include a customer ID.";
      }

      if (new Set(customerIds).size !== customerIds.length) {
        return "Duplicate customer IDs are not allowed.";
      }

      const invalidCustomer = this.selectedCustomerRows.find((customer) => !this.isSelectedCustomerReady(customer));
      if (invalidCustomer) {
        return "Each selected customer must have an available split option selected.";
      }

      return null;
    },

    async submitGenerateRequest() {
      const validationMessage = this.validateGenerateRequest();
      if (validationMessage) {
        this.$message.error(validationMessage);
        return;
      }

      const request = {
        mp_id: this.selectedMpId,
        category: this.selectedCategory,
        customers: this.selectedCustomerRows.map((customer) => ({
          customer_id: this.getCustomerId(customer),
          split_by: this.selectedSplits[this.getCustomerKey(customer)],
        })),
      };

      this.generating = true;
      try {
        const response = await UserService.generateSessionsWithAbatement(request);
        // this.generatedFiles = response.data.files || [];
        this.$message.success(response.data.message || "Session export files generated successfully.");
      } catch (error) {
        this.handleApiError(error, "Failed to generate session export files.");
      } finally {
        this.generating = false;
      }
    },

    handleApiError(error, fallbackMessage) {
      if (error?.response?.status === 401) {
        this.$router.push({ path: "/" });
        return;
      }
      this.$message.error(error?.response?.data?.message || error?.message || fallbackMessage);
    },
  },
};
</script>
